import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// ========================= FORMULA BUILDER =========================
const FORMULA_TASKS = [
  {
    id: 1,
    title: "Извлечь первые 5 символов из A1",
    blocks: ["=", "ЛЕВСИМВ", "ПРАВСИМВ", "A1", "B1", "C1", "1", "2", "3", "4", "5"],
    solution: "=ЛЕВСИМВ(A1;5)",
    hint: "ЛЕВСИМВ — первые n символов.",
  },
  {
    id: 2,
    title: "Извлечь последние 4 символа из B1",
    blocks: ["=", "ЛЕВСИМВ", "ПРАВСИМВ", "A1", "B1", "C1", "4", "5", "3"],
    solution: "=ПРАВСИМВ(B1;4)",
    hint: "ПРАВСИМВ — последние n символов.",
  },
  {
    id: 3,
    title: "Соединить A1 и B1 через пробел с &",
    blocks: ["=", "&", "СЦЕПИТЬ", "A1", "B1", "C1", "\" \""],
    solution: '=A1&" "&B1',
    hint: "Текст в кавычках. Пробел — это \" \".",
  },
  {
    id: 4,
    title: "Первые 3 символа из C1",
    blocks: ["=", "ЛЕВСИМВ", "C1", "3", "5"],
    solution: "=ЛЕВСИМВ(C1;3)",
    hint: "ЛЕВСИМВ(текст;n).",
  },
];

const normalize = (s) =>
  s
    .replaceAll(/\s+/g, "")
    .replaceAll(",", ";")
    .replaceAll("\u00A0", "")
    .trim();

function Block({ label, onClick, disabled }) {
  return (
    <button
      type="button"
      className="px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-2xl shadow border text-sm sm:text-sm transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-md bg-white"
      onClick={() => onClick?.(label)}
      disabled={disabled}
      title={label}
    >
      {label}
    </button>
  );
}

function Pill({ label, onRemove, index }) {
  return (
    <div
      className="flex items-center gap-1 px-3 py-2 rounded-2xl bg-gray-100 border cursor-pointer hover:bg-white/70"
      role="button"
      aria-label={`Удалить блок ${index + 1}`}
      title="Удалить блок"
      onClick={onRemove}
    >
      <span className="font-mono text-xs sm:text-sm">{label}</span>
    </div>
  );
}

// ========================= ГЛАВНЫЙ КОМПОНЕНТ =========================
export default function ExcelPractice() {
  const [formulaIndex, setFormulaIndex] = useState(0);
  const [tokens, setTokens] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const taskF = FORMULA_TASKS[formulaIndex];
  const availableBlocks = useMemo(() => taskF.blocks, [taskF]);
  const currentFormula = useMemo(() => tokens.join(""), [tokens]);
  const navigate = useNavigate();

  const canUseBlockMultipleTimes = useMemo(
    () => new Set(["&", "1", "2", "3", "4", "5", "=", "\" \""]),
    []
  );

  const addBlock = (label) => {
    setFeedback(null);
    if (!canUseBlockMultipleTimes.has(label)) {
      const used = tokens.filter((t) => t === label).length;
      const allowed = availableBlocks.filter((t) => t === label).length;
      if (used >= allowed) return;
    }
    setTokens((p) => [...p, label]);
  };

  const backspace = () => {
    setFeedback(null);
    setTokens((p) => p.slice(0, -1));
  };
  const clearTokens = () => {
    setFeedback(null);
    setTokens([]);
  };
  const checkFormula = () => {
    const ok = normalize(currentFormula) === normalize(taskF.solution);
    setFeedback({ ok, msg: ok ? "Отлично! Формула собрана верно." : "Не совсем. Проверь скобки, точки с запятой и порядок блоков." });
  };
  const nextF = () => {
    setFormulaIndex((i) => (i + 1) % FORMULA_TASKS.length);
    setTokens([]);
    setFeedback(null);
  };
  const prevF = () => {
    setFormulaIndex((i) => (i - 1 + FORMULA_TASKS.length) % FORMULA_TASKS.length);
    setTokens([]);
    setFeedback(null);
  };


  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-100 to-slate-200 flex items-start justify-center px-3 sm:px-6 py-4">
      <div className="w-full max-w-5xl">
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="text-sm text-gray-600">Задание {formulaIndex + 1} из {FORMULA_TASKS.length}</div>
            <div className="text-xl font-semibold mt-1">{taskF.title}</div>
            <div className="text-sm text-gray-600 mt-1">Подсказка: {taskF.hint}</div>
          </div>
          <div className="flex gap-2">
            <button className="px-3 py-2 rounded-xl bg-white border shadow hover:shadow-md" onClick={prevF}>← Назад</button>
            <button className="px-3 py-2 rounded-xl bg-white border shadow hover:shadow-md" onClick={nextF}>Далее →</button>
          </div>
        </div>

        <div className="mb-6 p-3 sm:p-4 rounded-2xl bg-white border shadow-sm">
            <div className="text-xs sm:text-sm text-gray-500 mb-2">Собранная формула</div>
            <div className="min-h-14 sm:min-h-16 flex flex-wrap items-center gap-1.5 sm:gap-2 p-2 sm:p-3 rounded-xl bg-gray-50 border overflow-x-auto">
            {tokens.length === 0 ? (
              <span className="text-gray-400">Нажимайте на блоки ниже, чтобы собрать формулу…</span>
            ) : (
              tokens.map((t, i) => (
                <Pill key={i + t} label={t} index={i} onRemove={() => setTokens((prev) => prev.filter((_, idx) => idx !== i))} />
              ))
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <button className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-white border shadow hover:shadow-md" onClick={backspace}>⌫ Удалить последний</button>
            <button className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-white border shadow hover:shadow-md" onClick={clearTokens}>Очистить</button>
            <button className="px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-emerald-600 text-white shadow hover:shadow-md" onClick={checkFormula}>Проверить</button>
          </div>
          {feedback && (
            <div className={`mt-3 text-sm ${feedback.ok ? "text-emerald-700" : "text-rose-700"}`}>
              {feedback.msg}
              {!feedback.ok && (
                <div className="mt-1 text-gray-600">
                  Ожидается: <code className="px-2 py-1 rounded bg-gray-100 border">{taskF.solution}</code>
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="text-sm text-gray-500 mb-2">Доступные блоки</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {availableBlocks.map((b, idx) => {
              const usedCount = tokens.filter((t) => t === b).length;
              const poolCount = availableBlocks.filter((t) => t === b).length;
              const disabled = !canUseBlockMultipleTimes.has(b) && usedCount >= poolCount;
              return <Block key={idx + b} label={b} onClick={addBlock} disabled={disabled} />;
            })}
          </div>

          <div className="mt-8 text-xs text-gray-500">
            Поддерживаются локализованные функции Excel (русский): ЛЕВСИМВ, ПРАВСИМВ, ПСТР, ДЛСТР, СЖПРОБЕЛЫ, ПРОПНАЧ, ЗАМЕНИТЬ.
            Валидация не чувствительна к пробелам и запятым (они приводятся к точке с запятой).
          </div>
        </div>
      </div>
      <button
        onClick={() => navigate("/")}
        className="fixed bottom-3 left-3 sm:bottom-6 sm:left-6 z-50 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl bg-white/90 backdrop-blur border shadow hover:shadow-md"
        >
        ← На главную
      </button>
    </div>

  );
}
